import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { AlertCircle, AlertTriangle, ChevronDown, ChevronUp, Plus, Upload } from 'lucide-react';
import React, { useState } from 'react';
import { CSVWarning, generateExperienceFromCSV, parseCSV } from './csvParser';

interface CSVImportPageProps {
  experience: IExperienceTemplate;
  onExperienceUpdate: (updatedExperience: IExperienceTemplate, originalExperience?: IExperienceTemplate) => void;
}

export const CSVImportPage: React.FC<CSVImportPageProps> = ({ experience, onExperienceUpdate }) => {
  const [updatedExperience, setUpdatedExperience] = useState<IExperienceTemplate | null>(null);
  const [originalExperience, setOriginalExperience] = useState<IExperienceTemplate | null>(null);
  const [parsedCSVData, setParsedCSVData] = useState<any[] | null>(null);
  const [warnings, setWarnings] = useState<CSVWarning[]>([]);
  const [showWarnings, setShowWarnings] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const createBackup = (experience: IExperienceTemplate): IExperienceTemplate => {
    const { createdAt, updatedAt, ...safeExperience } = experience;
    return JSON.parse(JSON.stringify(safeExperience));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setOriginalExperience(createBackup(experience));
    setWarnings([]);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const csvContent = e.target?.result as string;
      
      // Parse CSV and collect initial warnings
      const parseResult = parseCSV(csvContent);
      const allWarnings = [...parseResult.warnings];
      
      setParsedCSVData(parseResult.data);
      
      // Generate experience and collect additional warnings
      const generateResult = generateExperienceFromCSV(experience, parseResult.data, false);
      allWarnings.push(...generateResult.warnings);
      
      setUpdatedExperience(generateResult.experience);
      setWarnings(allWarnings);
      setIsProcessing(false);
    };
    reader.readAsText(file);
  };

  const handleApplyChanges = () => {
    if (updatedExperience && originalExperience) {
      onExperienceUpdate(updatedExperience, originalExperience);
    }
  };

  const handleOverwriteChanges = () => {
    if (!originalExperience || !parsedCSVData) return;
    
    const overwriteResult = generateExperienceFromCSV(originalExperience, parsedCSVData, true);
    onExperienceUpdate(overwriteResult.experience, originalExperience);
  };

  const downloadJSON = () => {
    if (!updatedExperience) return;
    
    const dataStr = JSON.stringify(updatedExperience, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', 'updated-experience.json');
    linkElement.click();
  };

  const getAddedCounts = () => {
    if (!updatedExperience) return { tasks: 0, messages: 0, actors: 0, automations: 0, audiences: 0};
    
    // Count unique audiences (excluding "Everyone" which is always present)
    const audienceNames = new Set();
    [...updatedExperience.tasks, ...updatedExperience.messages.other, ...(updatedExperience.automations || [])].forEach(item => {
      if (item.audiences) {
        item.audiences.forEach(audience => {
          if (audience.name !== 'Everyone' && audience.name !== '') {
            audienceNames.add(audience.name);
          }
        });
      }
    });

    return {
      tasks: updatedExperience.tasks.length - experience.tasks.length,
      messages: updatedExperience.messages.other.length - experience.messages.other.length,
      actors: Object.keys(updatedExperience.supportingActors).length - Object.keys(experience.supportingActors).length,
      automations: (updatedExperience.automations?.length || 0) - (experience.automations?.length || 0),
      audiences: audienceNames.size,
    };
  };

  const groupedWarnings = warnings.reduce((acc, warning) => {
    if (!acc[warning.type]) {
      acc[warning.type] = [];
    }
    acc[warning.type].push(warning);
    return acc;
  }, {} as Record<string, CSVWarning[]>);

  const warningCount = warnings.filter(w => w.type === 'warning').length;
  const errorCount = warnings.filter(w => w.type === 'error').length;

  const counts = getAddedCounts();

  return (
    <Box>
      <Card variant="outlined" style={{ marginBottom: 16 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Experience: {experience.title}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {experience.tasks.length} tasks, {experience.messages.other.length} messages, {Object.keys(experience.supportingActors).length} supporting actors
          </Typography>
        </CardContent>
      </Card>
      
      <Paper 
        variant="outlined" 
        style={{ 
          padding: 32, 
          textAlign: 'center', 
          marginBottom: 16,
          border: '2px dashed #ccc'
        }}
      >
        <Upload size={48} style={{ color: '#999', marginBottom: 16 }} />
        <Box>
          <input
            id="csv-upload"
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <label htmlFor="csv-upload">
            <Button component="span" variant="contained" color="primary">
              Upload CSV to Add Items
            </Button>
          </label>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          Items will be added to the existing experience
        </Typography>
      </Paper>

      {isProcessing && (
        <Box textAlign="center" py={2}>
          <Typography>Processing CSV...</Typography>
        </Box>
      )}

      {/* Warnings Display */}
      {warnings.length > 0 && (
        <Card variant="outlined" style={{ marginBottom: 16 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <Typography variant="h6">
                  Import Issues Found
                </Typography>
                {errorCount > 0 && (
                  <Chip 
                    icon={<AlertCircle size={16} />}
                    label={`${errorCount} Error${errorCount !== 1 ? 's' : ''}`}
                    color="secondary"
                    size="small"
                  />
                )}
                {warningCount > 0 && (
                  <Chip 
                    icon={<AlertTriangle size={16} />}
                    label={`${warningCount} Warning${warningCount !== 1 ? 's' : ''}`}
                    style={{ backgroundColor: '#fff3cd', color: '#856404' }}
                    size="small"
                  />
                )}
              </Box>
              <Button
                onClick={() => setShowWarnings(!showWarnings)}
                size="small"
                endIcon={showWarnings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              >
                {showWarnings ? 'Hide' : 'Show'} Details
              </Button>
            </Box>
            
            <Collapse in={showWarnings}>
              <Box mt={2}>
                {errorCount > 0 && (
                  <Alert severity="error" style={{ marginBottom: 16 }}>
                    <AlertTitle>Errors ({errorCount})</AlertTitle>
                    <List dense>
                      {groupedWarnings.error?.map((warning, index) => (
                        <ListItem key={index} style={{ paddingLeft: 0 }}>
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <AlertCircle size={16} />
                          </ListItemIcon>
                          <ListItemText
                            primary={warning.message}
                            secondary={`Row ${warning.rowNumber} - ${warning.itemType}: ${warning.itemTitle}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                )}
                
                {warningCount > 0 && (
                  <Alert severity="warning">
                    <AlertTitle>Warnings ({warningCount})</AlertTitle>
                    <Typography variant="body2" style={{ marginBottom: 8 }}>
                      These issues were automatically resolved, but you may want to review them:
                    </Typography>
                    <List dense>
                      {groupedWarnings.warning?.map((warning, index) => (
                        <ListItem key={index} style={{ paddingLeft: 0 }}>
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <AlertTriangle size={16} />
                          </ListItemIcon>
                          <ListItemText
                            primary={warning.message}
                            secondary={`Row ${warning.rowNumber} - ${warning.itemType}: ${warning.itemTitle}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {updatedExperience && (
        <Box>
          <Card variant="outlined" style={{ marginBottom: 16, backgroundColor: '#f0f8f0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Items to Add:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={5} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" style={{ color: '#2e7d32' }}>
                      +{counts.tasks}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Tasks
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={5} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" style={{ color: '#2e7d32' }}>
                      +{counts.messages}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Messages
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={5} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" style={{ color: '#2e7d32' }}>
                      +{counts.automations}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Automations
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={5} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" style={{ color: '#2e7d32' }}>
                      +{counts.audiences}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Audiences
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={5} md={2}>
                  <Box textAlign="center">
                    <Typography variant="h4" style={{ color: '#2e7d32' }}>
                      +{counts.actors}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Actors
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box display="flex">
            <Button
              onClick={handleApplyChanges}
              variant="contained"
              color="primary"
              startIcon={<Plus size={16} />}
              disabled={errorCount > 0}
            >
              Add to Experience
            </Button>
            <Button
              onClick={handleOverwriteChanges}
              variant="outlined"
              color="secondary"
              startIcon={<Upload size={16} />}
              disabled={!originalExperience || !parsedCSVData || errorCount > 0}
            >
              Replace Experience
            </Button>
          </Box>
          
          {errorCount > 0 && (
            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
              Please fix the errors above before proceeding.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};